import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getConfig, getStatus } from "@/lib/api";
import { getAgentStatus } from "@/lib/agent";

const ConfigContext = createContext(null);

// En preview/publicada, el backend cloud NO puede alcanzar el llama-server del Mac.
// El puente válido es el agente local (igual que el Dashboard). Construimos la lista
// de modelos REALES a partir del estado que el agente reporta desde /v1/models.
function buildModelsFromAgentStatus(s) {
  if (!s) return [];
  const caps = [];
  if (s.capabilities?.chat) caps.push("chat");
  if (s.capabilities?.vision) caps.push("vision");
  const list = [];
  if (s.model?.available && s.model?.id) {
    list.push({
      id: s.model.id,
      type: "model",
      owned_by: null,
      status: "available",
      capabilities: caps,
      raw: s.model,
    });
  }
  for (const m of s.local_models || []) {
    if (m.available !== false) {
      list.push({
        id: m.id,
        type: m.type || "image",
        owned_by: "local",
        status: "available",
        capabilities: m.capabilities || [],
        raw: m,
      });
    }
  }
  return list;
}

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [models, setModels] = useState([]);
  const [status, setStatus] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState(null);

  const refreshConfig = useCallback(async () => {
    const data = await getConfig();
    setConfig(data);
    return data;
  }, []);

  const refreshStatus = useCallback(async () => {
    const data = await getStatus();
    setStatus(data);
    return data;
  }, []);

  const refreshModels = useCallback(async () => {
    setLoadingModels(true);
    setModelsError(null);
    try {
      // El agente reúne chat, imagen y vídeo también en localhost.
      const s = await getAgentStatus();
      const list = buildModelsFromAgentStatus(s);
      setModels(list);
      if (list.length === 0) {
        const llama = s?.components?.llama_server;
        setModelsError(
          llama && llama.running === false
            ? "llama-server no está en ejecución (según el agente local)."
            : llama?.error || "El agente local no reporta ningún modelo disponible."
        );
      }
      return { ok: list.length > 0, models: list, status: s };
    } catch (e) {
      setModels([]);
      setModelsError(
        e?.message
          ? `No se pudo obtener el estado del agente local: ${e.message}`
          : "No se pudo contactar con el agente local."
      );
    } finally {
      setLoadingModels(false);
    }
  }, []);

  useEffect(() => {
    refreshConfig();
    refreshModels();
  }, [refreshConfig, refreshModels]);

  const value = {
    config,
    setConfig,
    models,
    status,
    loadingModels,
    modelsError,
    refreshConfig,
    refreshStatus,
    refreshModels,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useAppConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useAppConfig must be used within ConfigProvider");
  return ctx;
}
